<template>
  <div class="car-model-info" :class="{ 'shake-animation': isShaking }">
    <div v-if="errorMessage" class="error-message top-error">
      {{ errorMessage }}
    </div>
    <!-- 骨架屏展示 -->
    <SkeletonLoader v-if="loading" />
    <!-- 数据展示 -->
    <div class="info-content" v-else-if="data">
      <!-- 两列信息展示区域 -->
      <div class="info-grid">
        <div
          v-for="(row, rowIndex) in infoRows"
          :key="rowIndex"
          class="info-row"
        >
          <div
            v-for="item in row"
            :key="item.key"
            class="info-item"
          >
            <span class="label">{{ item.label }}</span>
            <span class="value">{{ formattedData[item.key] }}</span>
          </div>
        </div>
      </div>
      <!-- 车型图片展示区域 -->
      <div class="images-section">
        <div class="images-title">
          <span class="label">车型图片：</span>
          <span v-if="carModelImages.length > 0" class="value">车型外观/内饰/空间</span>
          <span v-else class="no-images">--</span>
        </div>
        <div class="images-grid" v-if="carModelImages.length > 0">
          <div
            v-for="(img, index) in carModelImages"
            :key="index"
            class="image-item"
          >
            <el-image
              :src="img.url"
              fit="cover"
              :preview-src-list="carModelPicList"
              :initial-index="index"
            />
            <span class="image-label">{{ img.label }}</span>
          </div>
        </div>
      </div>
    </div>
    <!-- 空状态提示 -->
    <EmptyState v-else />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { OPERATION_TYPE_ENUM } from '../../../config/list'
import { judgeFuelConsumption, judgePureElectricDrivingRange } from './config'
import EmptyState from './EmptyState.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import { useCarModelValidation } from './useCarModelValidation'

const props = defineProps({
  data: {
    type: Object,
    default: () => null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const isShaking = ref(false)
const errorMessage = ref('')

// 使用提取的验证 hook
const { validateModelData, clearError, shake } = useCarModelValidation({
  errorMessage,
  isShaking,
  judgePureElectricDrivingRange,
  judgeFuelConsumption,
  OPERATION_TYPE_ENUM,
})

// 车型等级查询结果标识  0：未查询到，1：查询到
const CAR_LEVEL_FLAG_ENUM = {
  NOT_QUERY: 0,
  QUERY: 1,
}

// 显示 纯电续航里程： 能源类型为 纯电动、插电式混动、增程式混动、油电混合
const showPureElectricDrivingRange = computed(() => {
  const { energyType } = props.data
  return judgePureElectricDrivingRange(energyType)
})

// 显示 综合油耗： 能源类型为 汽油、插电式混动、增程式混动、油电混合、柴油、天然气
const showFuelConsumption = computed(() => {
  const { energyType } = props.data
  return judgeFuelConsumption(energyType)
})

const showCarLevelName = computed(() => props.data?.operationType === OPERATION_TYPE_ENUM.NETWORK)

// 车型图片列表（用于预览）
const carModelPicList = computed(() => (props.data?.carModelPics || []).map((item) => item.value))

// 车型图片数据（带标签）
const carModelImages = computed(() => (props.data?.carModelPics || []).map((item) => ({
  url: item.value,
  label: item.key,
})))

// 信息项按两列分组
const infoRows = computed(() => {
  const items = [
    { label: '车型年款：', key: 'modelYear' },
    showPureElectricDrivingRange.value && { label: '纯电续航里程：', key: 'pureElectricDrivingRange' },
    showFuelConsumption.value && { label: '综合油耗：', key: 'fuelConsumption' },
    { label: '运营类型：', key: 'operationTypeName' },
    showCarLevelName.value && { label: '网约车接单资质：', key: 'carLevelName' },
  ].filter(Boolean)

  const rows = []
  for (let i = 0; i < items.length; i += 2) {
    rows.push(items.slice(i, i + 2))
  }
  return rows
})

const formattedData = computed(() => {
  const { data } = props
  const { carLevelFlag, carLevelName } = data
  return {
    ...data,
    modelYear: formatValue(data?.modelYear, '款'),
    pureElectricDrivingRange: formatValue(data?.pureElectricDrivingRange, 'km'),
    fuelConsumption: formatValue(data?.fuelConsumption, 'L/100km'),
    carLevelName:
      CAR_LEVEL_FLAG_ENUM.QUERY === carLevelFlag && carLevelName
        ? carLevelName
        : '该车型暂无可接单类型，请与合作网约平台进行确认',
  }
})

// 格式化显示值
const formatValue = (value, unit = '') => {
  if (!value && value !== 0) return '--'
  return unit ? `${value}${unit}` : value
}

// 监听数据变化，自动验证
watch(
  () => props.data,
  (n, o) => {
    if (n === o) return
    validateModelData(props.data, props.loading)
  },
  { deep: true },
)

// 组件挂载时验证
onMounted(() => {
  validateModelData(props.data, props.loading)
})

const validate = () => {
  return validateModelData(props.data, props.loading)
}

// 暴露方法给父组件
defineExpose({
  shake,
  clearError,
  validate,
})
</script>

<style lang="scss" scoped>
@import '@/styles/transition.scss';

.car-model-info {
  padding: 16px;
  margin: 0 0 24px;
  background: #f3f3f4;
  border-radius: 8px;
  min-height: 200px;

  .error-message {
    color: #f56c6c;
    font-size: 12px;
    line-height: 1;
    padding: 4px 0;
    &.top-error {
      margin-bottom: 8px;
    }
  }

  // 公共文本样式
  .label {
    color: #606266;
    flex-shrink: 0;
  }
  .value {
    color: #303133;
    font-weight: 500;
  }
  .no-images {
    color: #909399;
  }

  .info-content {
    .info-row {
      display: flex;
      margin-bottom: 10px;
    }

    .info-item {
      display: flex;
      align-items: center;
      min-width: 200px;
      margin-right: 40px;
    }

    .images-section {
      margin-top: 10px;

      .images-title {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
      }

      .images-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 12px;
      }

      .image-item {
        display: flex;
        flex-direction: column;
        align-items: center;

        .el-image {
          width: 100%;
          height: 80px;
          border-radius: 4px;
          overflow: hidden;
          border: 1px solid #e4e7ed;
          background: #fff;
        }

        .image-label {
          margin-top: 6px;
          font-size: 12px;
          color: #909399;
          text-align: center;
        }
      }
    }
  }
}
</style>
